import React, { useEffect } from 'react';
import LoadingSpinner from './LoadingSpinner';

const INTERACTIVE_SELECTOR =
  'a, button, [role="button"], [role="link"], input, select, textarea, label, summary, [tabindex]:not([tabindex="-1"])';

/**
 * Browse-safe "saving" state for the new look.
 *
 * Unlike LoadingOverlay, this does NOT blur or cover the page — members can
 * still scroll and read product details while the box selection saves. Clicks,
 * form submits, and activation keys on interactive controls are blocked in
 * capture phase so nothing can be mutated mid-save.
 */
const SavingSelectionGuard = ({ message = 'Saving your selection...' }) => {
  useEffect(() => {
    document.body.classList.add('wg-saving-selection');

    const blockAction = (event) => {
      event.preventDefault();
      event.stopPropagation();
    };

    const blockIfInteractive = (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (target.closest(INTERACTIVE_SELECTOR)) {
        blockAction(event);
      }
    };

    const blockActivationKey = (event) => {
      if (event.key !== 'Enter' && event.key !== ' ' && event.key !== 'Spacebar') return;
      blockIfInteractive(event);
    };

    // Capture-phase so we beat React handlers. Touch/wheel left alone so
    // members can keep scrolling the page underneath.
    document.addEventListener('click', blockAction, true);
    document.addEventListener('auxclick', blockAction, true);
    document.addEventListener('dblclick', blockAction, true);
    document.addEventListener('contextmenu', blockAction, true);
    document.addEventListener('submit', blockAction, true);
    document.addEventListener('change', blockAction, true);
    document.addEventListener('keydown', blockActivationKey, true);

    return () => {
      document.body.classList.remove('wg-saving-selection');
      document.removeEventListener('click', blockAction, true);
      document.removeEventListener('auxclick', blockAction, true);
      document.removeEventListener('dblclick', blockAction, true);
      document.removeEventListener('contextmenu', blockAction, true);
      document.removeEventListener('submit', blockAction, true);
      document.removeEventListener('change', blockAction, true);
      document.removeEventListener('keydown', blockActivationKey, true);
    };
  }, []);

  return (
    <div
      className="pointer-events-none fixed inset-x-0 top-0 z-[60] flex justify-center px-4 pt-[max(0.75rem,env(safe-area-inset-top))]"
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      <div className="flex items-center gap-2.5 rounded-2xl bg-white px-4 py-2.5 text-sm text-[#544947] shadow-[0_12px_32px_-12px_rgba(84,73,71,0.28),0_2px_8px_rgba(84,73,71,0.08)] ring-1 ring-black/10 motion-reduce:animate-none">
        <LoadingSpinner size="sm" />
        <p className="font-medium normal-case tracking-normal">{message}</p>
      </div>
    </div>
  );
};

export default SavingSelectionGuard;
